import { useCallback, useEffect, useRef, useState } from "react";

import Modal from "@/components/molecules/Modal";
import { ENQUIRY_TYPE } from "@/consts";
import useScrollLock from "@/hooks/useScrollLock";
import {
  type EcommerceItem,
  type EcommerceParams,
  gaGenerateLead,
} from "@/utils/tracking";

import { EnquiryInfoProvider } from "./EnquiryInfoContext";
import { useCYOQuery } from "./api/useCreateYourOwnQuery";
import CreateYourOwnErrorView from "./components/CreateYourOwnErrorView";
import CreateYourOwnPersonalForm from "./components/CreateYourOwnPersonalForm";
import CreateYourOwnSuccessView from "./components/CreateYourOwnSuccessView";
import { ModalEvent } from "./eventEnums";
import Layout from "./layout";
import { EnquiryInfo, PersonalDetailsFormData } from "./types";

enum EnquiryStep {
  TRAVEL_DETAILS = "travel-details",
  PERSONAL_DETAILS = "personal-details",
  SUCCESS = "success",
  ERROR = "error",
}

const CYOModal = () => {
  const [open, setOpen] = useState(false);
  const [enquiryInfo, setEnquiryInfo] = useState<EnquiryInfo>(
    {} as EnquiryInfo,
  );
  const [step, setStep] = useState<EnquiryStep>(EnquiryStep.PERSONAL_DETAILS);
  const [personalInfo, setPersonalInfo] = useState<PersonalDetailsFormData>(
    {} as PersonalDetailsFormData,
  );
  const trackedEnquiryIds = useRef(new Set<number>());

  useScrollLock(open);

  const { isSubmittingCYO, submitCYO } = useCYOQuery({
    onSuccess: () => {
      setStep(EnquiryStep.SUCCESS);
    },
    onError: () => {
      setStep(EnquiryStep.ERROR);
    },
  });

  const handleClose = useCallback(() => {
    setOpen(false);
    setStep(EnquiryStep.PERSONAL_DETAILS);
  }, []);

  useEffect(() => {
    const eventHandler = (e: MessageEvent) => {
      if (e.origin !== window.location.origin) {
        return;
      }

      const { eventType, event } = e.data ?? {};
      if (!eventType) return;

      if (eventType === ModalEvent.EXIT) {
        handleClose();
        return;
      }

      if (eventType === ModalEvent.OPEN_MODAL) {
        const { enquiryTitle, enquiryPrice, enquiryNights } = event;
        setOpen(true);
        setEnquiryInfo({
          title: enquiryTitle,
          price: enquiryPrice,
          nights: enquiryNights,
        });
      }
    };

    window.addEventListener("message", eventHandler);

    return () => {
      window.removeEventListener("message", eventHandler);
    };
  }, [handleClose]);

  const handleSubmit = async (personalDetails: PersonalDetailsFormData) => {
    const payload = {
      email: personalDetails.email,
      title: personalDetails.title,
      first_name: personalDetails.first_name,
      last_name: personalDetails.last_name,
      dialing_code: personalDetails.dialing_code,
      mobile: personalDetails.mobile,
      receive_offers: personalDetails.receive_offers,
      date: personalDetails.arrival_date,
      group_size: Number(personalDetails.group_size),
      destination: enquiryInfo.title,
      estimated_price: enquiryInfo.price,
      nights: enquiryInfo.nights,
      url: window.location.href,

      type: ENQUIRY_TYPE.GENERAL,
    };

    try {
      const response = await submitCYO(payload);

      generateLead(personalDetails, response?.data?.id);
      setPersonalInfo(personalDetails);
    } catch {
      // Error state is handled by useCYOQuery.onError
    }
  };

  const generateLead = (
    personalDetails: PersonalDetailsFormData,
    enquiryId?: number | null,
  ) => {
    if (!personalDetails || enquiryId == null) {
      return;
    }

    if (trackedEnquiryIds.current.has(enquiryId)) {
      return;
    }

    const item: EcommerceItem = {
      affiliation: "CYO",
      itemId:
        "CYO-" + (enquiryInfo.title || "Create Your Own").replace(/\\s+/g, "-"),
      itemName: enquiryInfo.title || "Create Your Own",
      itemVariant: `${enquiryInfo.nights || 1} nights`,
      quantity: parseInt(personalDetails.group_size, 10) || 1,
    };

    const params: EcommerceParams = {
      items: [item],
    };

    gaGenerateLead(params);

    trackedEnquiryIds.current.add(enquiryId);
  };

  return (
    <Modal portalEl="#cyo-root" open={open} onClose={handleClose}>
      {open && (
        <EnquiryInfoProvider value={enquiryInfo}>
          {step === EnquiryStep.ERROR ? (
            <CreateYourOwnErrorView
              onRetry={() => setStep(EnquiryStep.PERSONAL_DETAILS)}
            />
          ) : (
            <Layout>
              {step === EnquiryStep.PERSONAL_DETAILS ? (
                <CreateYourOwnPersonalForm
                  isSubmitting={isSubmittingCYO}
                  onSubmit={handleSubmit}
                />
              ) : (
                <CreateYourOwnSuccessView personalDetails={personalInfo} />
              )}
            </Layout>
          )}
        </EnquiryInfoProvider>
      )}
    </Modal>
  );
};

export default CYOModal;
